// StartCustomCode - Merchant-owned swatch controller. No app API, storage, or cart writes.
export function selectionFor(product, variant) {
  return Object.fromEntries(product.options.map((name, i) => [name.toLowerCase(), variant?.options[i]]));
}

export function exactVariant(product, selection) {
  const normalized = Object.fromEntries(Object.entries(selection).map(([name, value]) => [name.toLowerCase(), value]));
  const matches = product.variants.filter(variant => product.options.every((name, i) =>
    normalized[name.toLowerCase()] !== undefined && normalized[name.toLowerCase()] === variant.options[i]));
  return matches.length === 1 ? matches[0] : null;
}

export function productVariantUrl(product, variant, origin) {
  const url = new URL(product.url, origin);
  if (url.origin !== origin || !url.pathname.includes('/products/')) return null;
  if (variant) url.searchParams.set('variant', variant.id);
  return url.pathname + url.search;
}

export function variantOnSale(variant) {
  return !!variant && Number.isFinite(variant.price) && Number.isFinite(variant.compare_at_price)
    && variant.compare_at_price > variant.price;
}

export function createRequestPool(limit = 4) {
  let active = 0;
  const queue = [];
  const drain = () => {
    while (active < limit && queue.length) {
      const { task, resolve, reject } = queue.shift();
      active++;
      Promise.resolve().then(task).then(resolve, reject).finally(() => { active--; drain(); });
    }
  };
  return task => new Promise((resolve, reject) => { queue.push({ task, resolve, reject }); drain(); });
}

export function validateBatch(payload, group, currency) {
  if (payload?.groupKey !== group.key || payload.currency !== currency || !Array.isArray(payload.products)) {
    throw new Error('Swatch data context mismatch');
  }
  const expected = new Set(group.members.map(member => member.handle));
  if (payload.products.some(product => !expected.has(product.handle) || !Number.isFinite(product.id)
      || !Array.isArray(product.options) || !Array.isArray(product.variants))) {
    throw new Error('Invalid swatch product data');
  }
  return payload.products;
}

const swatchRequests = createRequestPool(4);
const batchCache = new Map();
const productCache = new Map();
const contextKey = data => `${data.root || '/'}|${data.currency}|`;

function groupProducts(data, product) {
  const group = data.group;
  const context = contextKey(data);
  const cached = group.members.map(member => productCache.get(context + member.handle));
  if (cached.every(Boolean)) return Promise.resolve(cached);
  return Promise.all(Array.from({ length: group.batches }, (_, batch) => {
    const key = context + group.key + '|' + batch;
    if (!batchCache.has(key)) {
      const promise = swatchRequests(async () => {
        const url = new URL(`${data.root || '/'}products/${product.handle}`, location.origin);
        url.searchParams.set('view', `ohc-swatch-batch-${batch}`);
        const abort = new AbortController();
        const timer = setTimeout(() => abort.abort(), 12000);
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: abort.signal });
          if (!response.ok) throw new Error('Swatch data unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = html.querySelector('[data-ohc-batch-data]');
          if (!source) throw new Error('Swatch data unavailable');
          const records = validateBatch(JSON.parse(source.textContent), group, data.currency);
          for (const record of records) productCache.set(context + record.handle, record);
          return records;
        } finally { clearTimeout(timer); }
      });
      batchCache.set(key, promise);
      promise.catch(() => batchCache.delete(key));
    }
    return batchCache.get(key);
  })).then(batches => batches.flat());
}

let instance = 0;
if (typeof customElements !== 'undefined' && !customElements.get('ohc-swatches')) {
  customElements.define('ohc-swatches', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const data = this.querySelector('[data-ohc-swatch-data]');
      if (!data) return;
      try { this.data = JSON.parse(data.textContent); } catch { return; }
      this.product = this.data.products.find(product => product.id === this.data.productId);
      if (!this.product?.variants?.length) return;
      for (const product of this.data.products) productCache.set(contextKey(this.data) + product.handle, product);
      this.deferred = !!this.data.group && (this.data.deferred || this.data.group.large);
      this.groupLoaded = !this.deferred;
      const initial = this.product.variants.find(variant => variant.id === this.data.selectedVariantId);
      this.selection = selectionFor(this.product, initial);
      const options = { signal: this.abort.signal };
      if (this.dataset.context === 'product') {
        document.addEventListener('change', event => {
          if (this.picker?.contains(event.target)) this.readNativeSelection();
        }, options);
        document.addEventListener(`variant:change:${this.dataset.sectionId}:${this.product.id}`, event => {
          // Scope repeated/quick-view products to their own picker instance.
          if (event.target !== this.picker || !event.detail?.variant) return;
          this.selection = selectionFor(this.product, event.detail.variant);
          this.update();
        }, options);
        this.readNativeSelection();
        this.addEventListener('click', async event => {
          const link = event.target.closest('[data-ohc-pending]');
          if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          await this.loadGroup();
          if (this.isConnected && link.hasAttribute('href') && link.getAttribute('aria-disabled') !== 'true') location.assign(link.href);
        }, options);
        if (this.deferred) {
          if (typeof IntersectionObserver === 'undefined') this.loadGroup();
          else {
            this.observer = new IntersectionObserver(entries => {
              if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.loadGroup(); }
            }, { rootMargin: '400px' });
            this.observer.observe(this);
          }
        }
      } else {
        const id = ++instance;
        this.querySelectorAll('[data-ohc-option-name]').forEach((fieldset, index) => {
          fieldset.querySelectorAll('input').forEach(input => { input.name = `ohc-card-${id}-${index}`; });
        });
        this.addEventListener('change', event => this.changeCard(event), options);
        const card = this.closest('.grid-item.grid-product');
        // QuickAdd's event is non-bubbling; capture covers both existing modal paths.
        card?.addEventListener('quickshop:opened', () => this.restoreModalSelection(), { ...options, capture: true });
        this.update();
      }
    }

    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }

    get picker() {
      if (this.dataset.context !== 'product') return null;
      const scope = this.closest('.product-single__meta') || this.parentElement;
      return scope?.querySelector(`[data-ohc-native][data-product-id="${this.product?.id}"]`);
    }

    loadGroup() {
      if (this.groupLoaded || !this.data.group) return Promise.resolve();
      if (this.pendingGroup) return this.pendingGroup;
      const fieldset = this.querySelector('.ohc-colours');
      fieldset?.setAttribute('aria-busy', 'true');
      this.pendingGroup = groupProducts(this.data, this.product).then(records => {
        if (!this.isConnected) return;
        const products = new Map(records.map(product => [product.id, product]));
        products.set(this.product.id, this.product);
        this.data.products = [...products.values()];
        this.groupLoaded = true;
        this.update();
      }).catch(() => {
        // Keep mounted normal product links available if a temporary data request fails.
        console.warn('Native swatch data unavailable; product links remain available.');
      }).finally(() => { this.pendingGroup = null; fieldset?.removeAttribute('aria-busy'); });
      return this.pendingGroup;
    }

    readNativeSelection() {
      if (!this.picker) return;
      this.picker.querySelectorAll('[data-ohc-option-name]').forEach(fieldset => {
        this.selection[fieldset.dataset.ohcOptionName.toLowerCase()] = fieldset.querySelector('input:checked')?.value;
      });
      this.update();
    }

    update() {
      for (const link of this.querySelectorAll('[data-ohc-member]')) {
        const member = this.data.products.find(product => product.handle === link.dataset.ohcHandle || String(product.id) === link.dataset.ohcMember);
        if (!member) {
          if (this.groupLoaded && this.deferred) {
            link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true'); link.removeAttribute('data-ohc-pending');
            link.hidden = true;
            link.querySelector('[data-ohc-colour-price]').textContent = 'Unavailable';
          }
          continue;
        }
        link.removeAttribute('data-ohc-pending');
        const variant = exactVariant(member, this.selection);
        const sale = link.querySelector('[data-ohc-sale]');
        if (sale) sale.hidden = !variantOnSale(variant);
        const range = link.dataset.ohcPriceMode === 'range';
        const url = productVariantUrl(member, variant, location.origin);
        if ((variant || range) && url) {
          link.setAttribute('href', url);
          link.removeAttribute('aria-disabled');
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
        }
        link.hidden = !member.available && member.id !== this.product.id;
        link.toggleAttribute('data-ohc-sold-out', !member.available || !!variant && !variant.available);
        const image = link.querySelector('img');
        const sourceImage = member.swatchImages?.[Number(link.dataset.ohcImageIndex)] || member.swatchImages?.[0];
        if (image && sourceImage && image.getAttribute('src') !== sourceImage) { image.removeAttribute('srcset'); image.src = sourceImage; }
        link.querySelector('[data-ohc-colour-price]').textContent = range
          ? member.rangePriceText + (this.data.currency !== 'AUD' ? ` ${this.data.currency}` : '')
          : variant ? this.moneyText(variant) : 'Unavailable';
        link.querySelector('[data-ohc-colour-price]').title = link.querySelector('[data-ohc-colour-price]').textContent;
        link.querySelector('[data-ohc-colour-status]').textContent = variant && !variant.available
          ? 'Sold out in this size' : '';
      }
      const optionScope = this.picker || this;
      for (const fieldset of optionScope.querySelectorAll('[data-ohc-option-name]')) {
        const name = fieldset.dataset.ohcOptionName.toLowerCase();
        const selectedText = fieldset.querySelector('[data-ohc-option-value]');
        if (selectedText) selectedText.textContent = this.selection[name] ? `– ${this.selection[name]}` : '';
        for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
          const variant = exactVariant(this.product, { ...this.selection, [name]: input.value });
          const row = input.closest('label');
          row.toggleAttribute('data-unavailable', !variant?.available);
          const price = row.querySelector('[data-ohc-option-price]');
          if (price) price.textContent = variant ? this.moneyText(variant) : 'Unavailable';
          const status = row.querySelector('[data-ohc-option-status]');
          if (status) status.textContent = variant?.available ? '' : 'Sold out or unavailable';
        }
      }
    }

    moneyText(variant) {
      return variant.priceText + (this.data.currency !== 'AUD' ? ` ${this.data.currency}` : '');
    }

    async restoreModalSelection() {
      const selection = { ...this.selection };
      await customElements.whenDefined('block-variant-picker');
      if (!this.isConnected) return;
      const picker = document.querySelector(`tool-tip block-variant-picker[data-ohc-native][data-product-id="${this.product.id}"]`);
      if (!picker) return;
      let changed;
      picker.querySelectorAll('[data-ohc-option-name]').forEach(fieldset => {
        const value = selection[fieldset.dataset.ohcOptionName.toLowerCase()];
        const input = [...fieldset.querySelectorAll('input')].find(input => input.value === value);
        if (input && !input.checked) { input.checked = true; changed = input; }
      });
      changed?.dispatchEvent(new Event('change', { bubbles: true }));
    }

    changeCard(event) {
      if (!event.target.matches('input[type="radio"]')) return;
      const fieldset = event.target.closest('[data-ohc-option-name]');
      if (!fieldset) return;
      this.selection[fieldset.dataset.ohcOptionName.toLowerCase()] = event.target.value;
      const variant = exactVariant(this.product, this.selection);
      this.update();
      if (!variant) return;
      const card = this.closest('.grid-item.grid-product');
      if (!card) return;
      card.dataset.ohcSelectedVariant = variant.id;
      for (const link of card.querySelectorAll('a[href]')) {
        if (link.closest('ohc-swatches')) continue;
        const url = new URL(link.href, location.origin);
        if (url.origin === location.origin && url.pathname.endsWith(`/products/${this.product.handle}`)) {
          url.searchParams.set('variant', variant.id);
          link.href = url.pathname + url.search;
        }
      }
      const price = card.querySelector('[data-swatch-price]');
      const stack = price?.querySelector('layout-stack');
      if (stack) {
        const nodes = [];
        const priceNode = (text, compare = false) => {
          const node = document.createElement('span');
          node.className = 'element-text element-price element-text--heading-xs' + (compare ? ' element-text--line-through' : '');
          const parts = text.match(/^(.*)([.,])(\d{2})$/);
          if (price.dataset.superscript === 'true' && parts) {
            node.classList.add('element-text--price-superscript');
            const amount = document.createElement('span'); amount.textContent = parts[1];
            const decimals = document.createElement('sup'); decimals.textContent = parts[3];
            node.append(amount, decimals);
          } else node.textContent = text;
          return node;
        };
        nodes.push(priceNode(variant.priceText));
        if (this.data.currency !== 'AUD') {
          const currency = document.createElement('span');
          currency.className = 'price-currency-code'; currency.textContent = this.data.currency; nodes.push(currency);
        }
        if (variant.compare_at_price > variant.price) {
          nodes.push(priceNode(variant.compareText, true));
          if (price.hasAttribute('data-save-enabled')) {
            const saved = variant.compare_at_price - variant.price;
            const label = document.createElement('span'); label.className = 'element-text element-text--body-sm';
            label.style.color = 'var(--color-text-savings)';
            label.textContent = 'Save ' + (price.dataset.saveType === 'percent'
              ? Math.round(saved * 100 / variant.compare_at_price) + '%'
              : new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: this.data.currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: saved % 100 ? 2 : 0 }).format(saved / 100));
            nodes.push(label);
          }
        }
        stack.replaceChildren(...nodes);
      }
      if (variant.image) {
        for (const image of card.querySelectorAll('.grid-product__image-wrap img')) {
          image.removeAttribute('srcset'); image.removeAttribute('data-srcset');
          image.src = variant.image; image.dataset.src = variant.image;
        }
        card.classList.add('ohc-card-image-selected');
      }
      for (const add of card.querySelectorAll('at-add-to-cart')) {
        add.dataset.variantId = variant.id; add.dataset.available = String(variant.available);
      }
      for (const input of card.querySelectorAll('form input[name="id"]')) input.value = variant.id;
      const status = this.querySelector('[data-ohc-card-status]');
      status.textContent = `${variant.options.join(', ')}, ${this.moneyText(variant)}, ${variant.available ? 'in stock' : 'sold out'}`;
      this.dispatchEvent(new CustomEvent('ohc:variant-change', { bubbles: true, detail: { productId: this.product.id, variant } }));
    }
  });
}
// EndCustomCode
